import { useState, type FormEvent } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import { Lock, Mail } from "lucide-react";
import { Turnstile } from "@marsidev/react-turnstile";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Logo } from "@/components/shared/Logo";
import { ROLE_HOME, APP_TAGLINE } from "@/lib/constants";

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as string;

export default function Login() {
  const { signIn, session, role, loading } = useAuth();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);

  // Single redirect path — render-time Navigate is sufficient.
  // Previously there was also a useEffect calling navigate() for the same
  // condition, which caused replaceState to fire twice on the same tick and
  // accumulated into the "history.replaceState > 100 times" Sentry error.
  if (!loading && session && role) {
    const target =
      (location.state as { from?: { pathname: string } } | null)?.from
        ?.pathname ?? ROLE_HOME[role] ?? "/";
    return <Navigate to={target} replace />;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    const { error } = await signIn(email.trim(), password, captchaToken ?? undefined);
    setSubmitting(false);
    if (error) {
      setCaptchaToken(null);
      setError(
        error.message === "Invalid login credentials"
          ? "Email or password is incorrect."
          : error.message,
      );
    }
  }

  return (
    <div className="min-h-screen bg-black grid place-items-center px-6 py-12 relative overflow-hidden">
      <div aria-hidden className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(180,140,60,0.08)_0%,_transparent_70%)]" />
      </div>
      <div className="w-full max-w-sm relative z-10">
        <div className="flex flex-col items-center mb-8 gap-3">
          <Logo className="h-16 w-auto" />
          <p className="text-xs tracking-[0.2em] text-muted uppercase text-center">
            {APP_TAGLINE}
          </p>
        </div>
        <div className="bg-surface border border-border rounded-2xl px-8 py-8 shadow-2xl">
          <h1 className="text-xl font-semibold text-primary mb-6">Sign in</h1>
          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              type="email"
              name="email"
              label="Email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              leftIcon={<Mail className="h-4 w-4" />}
              autoComplete="email"
              required
              disabled={submitting}
            />
            <Input
              type="password"
              name="password"
              label="Password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              leftIcon={<Lock className="h-4 w-4" />}
              autoComplete="current-password"
              required
              disabled={submitting}
            />
            {error && (
              <div className="text-xs text-danger bg-[#3a1a1a] border border-[#552323] rounded-md px-3 py-2">
                {error}
              </div>
            )}
            <div className="flex justify-center">
              <Turnstile
                siteKey={TURNSTILE_SITE_KEY}
                onSuccess={(token) => setCaptchaToken(token)}
                onExpire={() => setCaptchaToken(null)}
                onError={() => setCaptchaToken(null)}
                options={{ theme: "dark", size: "normal" }}
              />
            </div>
            <Button
              type="submit"
              block
              size="lg"
              loading={submitting}
              disabled={!email || !password || !captchaToken}
            >
              Sign in
            </Button>
            <div className="flex items-center justify-between text-xs">
              <Link to="/forgot-password" className="text-muted hover:text-gold">
                Forgot password?
              </Link>
              <Link to="/invite" className="text-muted hover:text-gold">
                Have an invite code?
              </Link>
            </div>
            <p className="text-[11px] text-center text-dim">
              By continuing you agree to Nestd's terms of service.
            </p>
          </form>
        </div>
        <div className="mt-5 text-center">
          <p className="text-xs text-muted">
            Don't have an account?{" "}
            <Link to="/register" className="text-gold font-semibold hover:underline">
              Register here
            </Link>
          </p>
        </div>
        <p className="mt-6 text-center text-[11px] text-dim">
          Powered by Nestd · Built for African student housing
        </p>
      </div>
    </div>
  );
}